/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild, EventEmitter, Input, OnDestroy } from '@angular/core';
import { Subscription } from "rxjs";
import { ImageService } from "app/services/image.service";
import { ObjectInstance } from 'app/models/object-instance';
import { DraggableDirective } from 'app/directives/draggable.directive';
import { DisplayModule } from 'app/shared/display.module';

declare var $: any;

@Component({
  selector: 'ef-instance-locate-popup',
  templateUrl: './instance-popup-dialog.component.html'
})
export class InstancePopupDialogComponent implements OnInit, OnDestroy {

  /** Emitter used to hide the popup from the Parent.  Needs to be done inside here so that the drag directive can be reset */
  @Input()
  hideEmitter: EventEmitter<boolean>;

  /** Instance whose information is being displayed in the popup */
  @Input()
  instance: ObjectInstance;

  /** Unique ID to given the popup.  Parent references it when showing / hiding the dialog */
  @Input()
  dialogId: string;

  /** Draggable Directive added to that the popup is draggable.  Referenced so that it can be reset when the popup is hidden */
  @ViewChild(DraggableDirective) draggableDirective;

  /** Subscription to the "hide" events from the Parent */
  hideSubscription: Subscription;

  /** Flag used to indicate whether the "Full Details" section should be displayed or not */
  displayFullDetailsFlag: boolean = false;


  /**
   * Constructor
   */
  constructor(
    private imageService: ImageService
  ) { }

  /**
   * OnInit - Called after component initialization.
   * Used to create the "hideSubscription" if the "callHideEmitter" was defined which is used to hide the Dialog. 
   */
  ngOnInit(): void {
    if (this.hideEmitter != null) {
      this.hideSubscription = this.hideEmitter.subscribe(value => {
        this.hide();
      });
    }
  }

  /**
   * OnDestroy - Called before component destruction.
   * Used to clean up  / destroy the "hideSubscription" if needed 
   */
  ngOnDestroy(): void {
    if (this.hideSubscription != null) {
      this.hideSubscription.unsubscribe();
      this.hideSubscription = null;
    }
  }

  /**
   * Hides the Instance Popup Dialog
   */
  public hide(): void {
    this.hideFullDetails();
    $(`#${this.dialogId}`).hide();
    this.draggableDirective.reset();
  }

  /**
   * Returns the ID that should be used for the Dialog Header HTML Element
   */
  getIdForDialogHeader(): string {
    return `${this.dialogId}_header`;
  }

  /**
   * Returns the URL string that should be used for the Instance's Image HTML element
   */
  getPathForImage(): string {
    return this.imageService.getImageSourceForObjectInstance(this.instance);
  }

  /**
   * Returns the URL string that should be used for the Instance's Icon HTML element
   */
  getPathForIcon(): string {
    return this.imageService.getIconSourceForObjectInstance(this.instance);
  }

  /**
   * Returns the Display String for the given measurement
   */
  getDisplayForMeasurement(valueInMeters: number): string {
    return DisplayModule.getDisplayForMeasurement(valueInMeters);
  }

  /**
   * Returns a boolean indicating if the Asset Image is present.
   * Used in the HTML to display the Image or not. 
   */
  public isImagePresent(): boolean {
    return this.instance?.image != null;
  }

  /**
   * Returns a boolean indicating if the "Show Full Details" / "Hide Full Details"
   * Link / Panels need to be shown.  This returns true if the corresponding Asset
   * has Data for a Property that is not a "Main Display" property.
   */
  public isShowFullDetailsNeeded(): boolean {
    if (this.instance?.data?.length > 0) {
      for (let datum of this.instance.data) {
        if (!datum.property?.mainDisplay) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Used to show the Full Details of the Asset.
   * This includes the data for the properties that are not marked "main display".
   */
  showFullDetails(): void {
    this.displayFullDetailsFlag = true;
    setTimeout(this.resizeDialog.bind(this), 0);    // Resize the Dialog, done on a background thread to give Angular time to display the Full Details section so it can be accounted for in the height calculation. TODO: There's probably a better way to do this...
  }

  /**
   * Used to hide the Full Details of the Asset.
   */
  hideFullDetails(): void {
    this.displayFullDetailsFlag = false;
    setTimeout(this.resizeDialog.bind(this), 0);    // Resize the Dialog, done on a background thread to give Angular time to hide the Full Details section so it can be accounted for in the height calculation. TODO: There's probably a better way to do this...
  }

  /**
   * Resizes the Dialog Height based on the Document's Height.
   * 
   * The below piece of code is needed since the top level "role='dialog'" <div> element used for the Dialog
   * is being padded with some extra invisible space underneath the Dialog.  This is preventing click events from
   * being detected on Icons that are visible underneath the Dialog since it was being blocked by the invisible part.
   * This resizes the outer "role='dialog'" <div> element to that of the inner "role='document'" <div> element. 
   * Hacky, but works.
   */
  private resizeDialog(): void {
    $(`#${this.dialogId}`)?.css('height', $(`#${this.dialogId}_Document`)?.height() + 1);
  }

}
